/* eslint-disable max-len */
import { Box, Grid, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import nodataimagenetworks from '../../../../../../../../assets/Resources/nodataImageNetworks.svg';
import NoData from '../../../../../../../../components/Common/NoData';
import MainCard from '../../../../../../../../UiComponents/Cards/MainCard';
import ButtonTabs from '../../../../../../../ResourcesRevised/Components/Resuable/ButtonTabs';
import { ContentTypeKey } from '../../../../../../../ResourcesRevised/types';
import CategoryBanner from '../../StatusContentSection/CategoriesView/CategoryBanner';
import ArticlesCardsList from './ArticlesCardsList';
import CategoryCardsList from './CategoryCardsList';
import DocumentsCardsList from './DocumentsCardsList';
import ImagesCardsList from './ImagesCardsList';
import PlaylistsCardsList from './PlaylistsCardsList';
import PodcastsCardsList from './PodcastsCardsList';
import useContentPermissions from './useContentPermissions';
import useResourceHelper from './useResourceHelper';
import VideosCardsList from './VideosCardsList';
interface ContentCardsContainerProps { // Accept any type for setContentView to accommodate different implementations
	StatusVisible?: boolean;
	ContentView?: any; // Accept any type for ContentView to accommodate different implementations
	currentTxt?: string;
	showPath?: boolean | string; // Accept either boolean or string
	// ContentCategoryType?: boolean; // Add missing prop
	// onContentClick?: (contentType: string, contentId: string) => void;
	// eslint-disable-next-line no-unused-vars
	noDataCallback?: (val?: boolean) => void;
}

// common component for all content cards
// previosuly using ContentViewCard.tsx file
const ContentCardsContainer: React.FC<ContentCardsContainerProps> = ({
	// setContentView,
	StatusVisible = true,
	ContentView = 'grid',
	// currentTxt = '',
	showPath = false,
	// ContentCategoryType = false,
	noDataCallback
	// onContentClick
}) =>
{
	// const { language } = useContext(LanguageContext);
	const [ searchParams ] = useSearchParams();
	const navigate = useNavigate();
	const type = useParams()?.type;
	// Define allowed status values
	const allowedStatusValues = [ 'drafts', 'published', 'unpublished', 'scheduled', 'uncategorized' ];

	// State for managing selections and UI
	const [ selectedItems, setSelectedItems ] = useState<string[]>([]);
	// const [ isDialogOpen, setIsDialogOpen ] = useState(false);
	const location = useLocation();
	const [ selectAllRequested, setSelectAllRequested ] = useState(false);
	const { categoryId } = useParams();
	// Get status from URL search parameters
	// const status = searchParams.get('status') ||
	// (location.pathname.includes('resource/playlist') || (location.pathname.includes('resource/network/companies/') && location.pathname.includes('/playlist'))
	// 	? 'playlists'
	// 	: '');
	const status = useMemo(() =>
	{
		const statusParam = searchParams.get('status');

		if (statusParam)
		{
			return statusParam;
		}
		else if (
			location.pathname.includes('resource/playlist') ||
			(location.pathname.includes('resource/network/companies/') && location.pathname.includes('/playlist'))
		)
		{
			return 'playlists';
		}
		else
		{
			return '';
		}
	}, []);

	// Map URL type to ContentTypeKey and set initial selectedContent
	const getInitialContentType = (): ContentTypeKey =>
	{
		if (status === 'playlists')
		{
			return 'playlists';
		}

		// Map URL types to content type keys
		const contentTypeMap: Record<string, ContentTypeKey> = {
			articles      : 'articles',
			videos        : 'videos',
			podcasts      : 'podcasts',
			documents     : 'documents',
			images        : 'images',
			playlist      : 'playlists',
			// categories   : 'categories',
			subcategories : 'subcategories'
		};

		return contentTypeMap[type || ''] || 'all';
	};

	const [ selectedContent, setSelectedContent ] = useState<ContentTypeKey>(getInitialContentType());

	const { isAdminView } = useResourceHelper();
	const {
		// isArticlesEnabled,
		// isImagesEnabled,
		// isVideosEnabled,
		// isDocumentsEnabled,
		// isPodcastsEnabled,
		// isPlaylistsEnabled,
		isResourcesEnabled
		// isAllContentDisabled
	} = useContentPermissions();

	// Helper function to render NoData component
	const renderNoData = (alt: string, head: string, para: string) => (
		<MainCard sx={{ width: '100%', overflow: 'visible' }}>
			<Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={8} textAlign='center'>
				<NoData src1={nodataimagenetworks} alt={alt} noDataHead={head} noDataPara={para} searchVal='' checkChangedDates data={[]} />
			</Box>
		</MainCard>
	);

	// Validate status parameter and redirect if invalid
	useEffect(() =>
	{
		const statusFromUrl = searchParams.get('status');

		// Only validate if status exists in URL and is not empty
		if (statusFromUrl && statusFromUrl.trim() !== '')
		{
			// Check if status is not in allowed values and not 'playlists' (special case)
			if (!allowedStatusValues.includes(statusFromUrl) && statusFromUrl !== 'playlists')
			{
				toast.error('Invalid status parameter');
				// Redirect to main page
				navigate(isAdminView ? '/businessSettings/content' : '/resource', { replace: true });
			}
		}
	}, [ searchParams, isAdminView ]);

	// Update selectedContent when URL type changes
	useEffect(() =>
	{
		const newContentType = getInitialContentType();

		setSelectedContent(newContentType);
	}, [ type, status ]);

	// State for tracking content counts to help with "select all" functionality
	// const [ contentCounts, setContentCounts ] = useState({
	// 	articles  : 0,
	// 	videos    : 0,
	// 	images    : 0,
	// 	podcasts  : 0,
	// 	documents : 0,
	// 	playlists : 0
	// });

	// Total count of all content items, used for select all functionality
	// const totalContentCount = Object.values(contentCounts).reduce((a, b) => a + b, 0);

	// Items per view based on screen size
	const [ itemsPerView, setItemsPerView ] = useState<number>(5);

	// Track if at least one section has data
	const [ hasAnyData, setHasAnyData ] = useState(true);
	const dataTracker = useRef<any>({});

	// Initialize dataTracker with all expected sections on mount or when status/type changes
	useEffect(() =>
	{
		// Determine which sections should be tracked based on status and type
		const expectedSections: string[] = [];

		if (status === 'playlists')
		{
			expectedSections.push('playlists');
		}
		else if (type === 'favorites')
		{
			expectedSections.push('playlists', 'articles', 'videos', 'images', 'podcasts', 'documents');
		}
		else
		{
			// When status is not 'playlists', these sections render
			expectedSections.push('articles', 'videos', 'images', 'podcasts', 'documents');
		}

		// Initialize all expected sections with true (optimistic - assume data exists)
		// This allows components to render, fetch data, and report back
		const newTracker: Record<string, boolean> = {};

		expectedSections.forEach((section) =>
		{
			newTracker[section] = true;
		});
		dataTracker.current = newTracker;
		setHasAnyData(true); // Assume data exists until proven otherwise
		if (noDataCallback)
		{
			noDataCallback(false);
		}
	}, [ status, type, noDataCallback ]);

	// Update items per view based on screen size
	useEffect(() =>
	{
		const updateItemsPerView = () =>
		{
			if (window.innerWidth <= 600)
			{
				setItemsPerView(1);
			}
			else if (window.innerWidth >= 601 && window.innerWidth <= 899)
			{
				setItemsPerView(3);
			}
			else if (window.innerWidth >= 900 && window.innerWidth <= 1200)
			{
				setItemsPerView(3);
			}
			else if (window.innerWidth >= 1200)
			{
				setItemsPerView(5);
			}
		};

		updateItemsPerView();
		window.addEventListener('resize', updateItemsPerView);

		return () =>
		{
			window.removeEventListener('resize', updateItemsPerView);
		};
	}, []);

	// Handle content counts updates from child components
	// const handleContentCountUpdate = (type: ContentTypeKey, count: number) =>
	// {
	// 	if (type !== 'all')
	// 	{
	// 		setContentCounts((prev) => ({
	// 			...prev,
	// 			[type] : count
	// 		}));
	// 	}
	// };

	// Single callback to track if sections have data
	const reportSectionData = useCallback((section: string, hasData: boolean) =>
	{
		// Update the section's data status
		dataTracker.current[section] = hasData;

		// Evaluate overall data status and only update when state actually changes
		if (Object.values(dataTracker.current).some((value) => value))
		{
			setHasAnyData(true);
			if (noDataCallback)
			{
				noDataCallback(false);
			}
		}
		else if (Object.values(dataTracker.current).every((value) => !value))
		{
			setHasAnyData(false);
			if (noDataCallback)
			{
				noDataCallback(true);
			}
		}
	}, [ noDataCallback ]);

	// // Handle select all checkbox change
	// const handleCheckboxChangeAll = (
	// 	event: React.ChangeEvent<HTMLInputElement>
	// ) =>
	// {
	// 	event.stopPropagation();
	// 	const checked = event.target.checked;

	// 	if (checked)
	// 	{
	// 		// Set selectAllRequested to true, which will trigger the child components
	// 		// to add their IDs to the selection
	// 		setSelectAllRequested(true);
	// 	}
	// 	else
	// 	{
	// 		// Clear all selections and reset selectAllRequested
	// 		setSelectedItems([]);
	// 		setSelectAllRequested(false);
	// 	}
	// };

	// Handle individual checkbox changes
	const handleCheckboxChange = (
		itemId: string,
		// event: React.ChangeEvent<HTMLInputElement>,
		checked: boolean
	) =>
	{
		// event.stopPropagation();
		// const itemId = event.target.id;

		let newSelected = [ ...selectedItems ];

		if (checked)
		{
			// Add the ID to the selected items
			newSelected = [ ...selectedItems, itemId ];
		}
		else
		{
			// Remove the ID from the selected items
			newSelected = selectedItems.filter((id) => id !== itemId);
		}

		setSelectedItems(newSelected);

		// If we're unselecting an item, we're no longer in "select all" mode
		if (!checked && selectAllRequested)
		{
			setSelectAllRequested(false);
		}
	};

	// Handle bulk selection from child components
	const handleBulkSelectionUpdate = (ids: string[], add: boolean) =>
	{
		if (add)
		{
			// Add all IDs that aren't already selected
			setSelectedItems((prev) =>
			{
				const uniqueIds = new Set([ ...prev, ...ids ]);

				return Array.from(uniqueIds);
			});
		}
		else
		{
			// Remove these IDs from the selection
			setSelectedItems((prev) => prev.filter((id) => !ids.includes(id)));
		}
	};

	// Handle content type change from ButtonTabs
	const handleContentTypeChange = (key: ContentTypeKey) =>
	{
		setSelectedContent(key === selectedContent ? 'all' : key);
	};

	// Show disabled message if resources are not enabled
	if (!isResourcesEnabled)
	{
		return renderNoData('Configuration Disabled', 'Configuration Disabled', 'Resources module is currently disabled.');
	}

	// Note: Removed all content disabled check - users can view content even if modules are disabled

	// Note: Removed playlist enablement check - users can view playlists even if module is disabled

	// Check if we should show "No data found"
	const shouldShowNoData = !hasAnyData;

	return (
		<>
			<MainCard sx={{ width: '100%', overflow: 'visible' }}>
				{!isAdminView && categoryId && <CategoryBanner/>}
				{location.pathname !== '/resource/favourates' && status !== 'playlists' && (
					<Stack
						direction={'row'}
						width={'100%'}
						alignItems={'center'}
						justifyContent={'space-between'}
						sx={{ mb: 2 }}
					>
						<ButtonTabs
							selected={selectedContent}
							onChange={handleContentTypeChange}
							noDataCallback={noDataCallback}
						/>
						{/* <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
							<Checkbox
								checked={selectAllRequested || (selectedItems.length === totalContentCount && totalContentCount > 0)}
								indeterminate={!selectAllRequested && selectedItems.length > 0 && selectedItems.length < totalContentCount}
								onChange={handleCheckboxChangeAll}
							/>
							<Typography variant='body2' onClick={() => setIsDialogOpen(true)}>
								{language?.slctAll}
							</Typography>
						</Stack> */}
					</Stack>
				)}

				{shouldShowNoData && (
					<Box
						sx={{
							display        : 'flex',
							justifyContent : 'center',
							alignItems     : 'center',
							minHeight      : '400px'
						}}
					>
						<NoData
							noDataHead='No data found'
							src1='/images/cardnodata.svg'
							heightimg='100px'
							widthimg='100px'
							color='lightTxt'
						/>
					</Box>
				)}

				<Box sx={{ display: shouldShowNoData ? 'none' : 'block' }}>
					{(status === 'playlists' || type === 'favorites') &&
					<Grid
						container
						direction={'column'}
						width={'100%'}
						overflow={'visible'}
					>
						{/* Playlists section - component returns null when no data */}
						{(selectedContent === 'all' || selectedContent === 'playlists') && (
							<PlaylistsCardsList
								selectedItems={selectedItems}
								handleCheckboxChange={handleCheckboxChange}
								handleBulkSelectionUpdate={handleBulkSelectionUpdate}
								onCountUpdate={(count) => reportSectionData('playlists', count > 0)}
								selectAll={selectAllRequested}
								showPath={showPath as boolean}
								StatusVisible={StatusVisible}
								ContentView={ContentView}
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'playlists'}
								handleViewAll={handleContentTypeChange}
							/>
						)}
					</Grid>}
					{status !== 'playlists' &&
					<Grid
						container
						direction={'column'}
						width={'100%'}
						overflow={'visible'}
					>
						{/* Subcategories section - visible when inside a category */}
						{categoryId && (selectedContent === 'all' || selectedContent === 'subcategories') && (
							<CategoryCardsList
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'subcategories'}
								handleViewAll={handleContentTypeChange}
								subcategoryOnly
								onCountUpdate={(count) => reportSectionData('subcategories', count > 0)}
							/>
						)}

						{/* Articles section - component returns null when no data */}
						{(selectedContent === 'all' || selectedContent === 'articles') && (
							<ArticlesCardsList
								selectedItems={selectedItems}
								handleCheckboxChange={handleCheckboxChange}
								handleBulkSelectionUpdate={handleBulkSelectionUpdate}
								onCountUpdate={(count) => reportSectionData('articles', count > 0)}
								selectAll={selectAllRequested}
								showPath={showPath as boolean}
								StatusVisible={StatusVisible}
								ContentView={ContentView}
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'articles'}
								handleViewAll={handleContentTypeChange}
							/>
						)}

						{/* Videos section - component returns null when no data */}
						{(selectedContent === 'all' || selectedContent === 'videos') && (
							<VideosCardsList
								selectedItems={selectedItems}
								handleCheckboxChange={handleCheckboxChange}
								handleBulkSelectionUpdate={handleBulkSelectionUpdate}
								onCountUpdate={(count) => reportSectionData('videos', count > 0)}
								selectAll={selectAllRequested}
								showPath={showPath as boolean}
								StatusVisible={StatusVisible}
								ContentView={ContentView}
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'videos'}
								handleViewAll={handleContentTypeChange}
							/>
						)}

						{/* Images section - component returns null when no data */}
						{(selectedContent === 'all' || selectedContent === 'images') && (
							<ImagesCardsList
								selectedItems={selectedItems}
								handleCheckboxChange={handleCheckboxChange}
								handleBulkSelectionUpdate={handleBulkSelectionUpdate}
								onCountUpdate={(count) => reportSectionData('images', count > 0)}
								selectAll={selectAllRequested}
								showPath={showPath as boolean}
								StatusVisible={StatusVisible}
								ContentView={ContentView}
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'images'}
								handleViewAll={handleContentTypeChange}
							/>
						)}

						{/* Podcasts section - component returns null when no data */}
						{(selectedContent === 'all' || selectedContent === 'podcasts') && (
							<PodcastsCardsList
								selectedItems={selectedItems}
								handleCheckboxChange={handleCheckboxChange}
								handleBulkSelectionUpdate={handleBulkSelectionUpdate}
								onCountUpdate={(count) => reportSectionData('podcasts', count > 0)}
								selectAll={selectAllRequested}
								showPath={showPath as boolean}
								StatusVisible={StatusVisible}
								ContentView={ContentView}
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'podcasts'}
								handleViewAll={handleContentTypeChange}
							/>
						)}

						{/* Documents section - component returns null when no data */}
						{(selectedContent === 'all' || selectedContent === 'documents') && (
							<DocumentsCardsList
								selectedItems={selectedItems}
								handleCheckboxChange={handleCheckboxChange}
								handleBulkSelectionUpdate={handleBulkSelectionUpdate}
								onCountUpdate={(count) => reportSectionData('documents', count > 0)}
								selectAll={selectAllRequested}
								showPath={showPath as boolean}
								StatusVisible={StatusVisible}
								ContentView={ContentView}
								itemsPerView={itemsPerView}
								viewAll={selectedContent === 'documents'}
								handleViewAll={handleContentTypeChange}
							/>
						)}
					</Grid>}
				</Box>
			</MainCard>
			{/* 
			<PublishingProgressDialog
				open={isDialogOpen}
				progress={80}
				total={90}
				onClose={() => setIsDialogOpen(false)}
			/> */}
		</>
	);
};

// Add display name
ContentCardsContainer.displayName = 'ContentCardsContainer';

export default ContentCardsContainer;